import { ValidationService } from './../service/validation.service';
import { Component, OnInit, Renderer2, HostListener, Inject, forwardRef  } from '@angular/core';
import { LocalStorageService } from '../local-storage.service';
import { firstValueFrom, Subject, tap } from 'rxjs';
import { CognitoService } from '../service/cognito.service';
import { TranslateService } from '@ngx-translate/core';
import { Client } from '../service/client.service';
import { ThemeService } from '../service/theme.service';
import { DistributorsService } from '../service/distributors.service';
import { NavigationService } from '../service/navigation.service';
import { SystemMessageService } from '../service/system-message.service';
import { FilterService } from '../service/filter.service';


@Component({
    selector: 'app-distributors-list',
    templateUrl: './distributors-list.component.html',
    styleUrls: ['./distributors-list.component.css'],
    standalone: false
})
export class DistributorsListComponent implements OnInit {

  // Variables for user filtering
  public mainContent: string = '';
  public userFilterTest: string ="";
  public userFilter: string ="";
  public filterBy: string = '';
  public userTest: string = "";
  public searchText: string = '';

  // Subject for search terms
  public searchTerms = new Subject<string>();

  // Arrays to store user data
  public filteredUserTest: any = [];
  public clientArray: any[] = [];
  public clientArrayToFilter: Client[] = [];

  // Flags for UI state
  public showList: boolean = false;
  public filterOn: boolean = false;
  public loadingMoreData = false;

  // Value to manage the pagination
  private itemsPerPage = 3;
  private loadedItems = 0;

  public clientsData: any[] = [];
  public distributorsData: any[] = [];
  public distributorsArray: any[] = [];
  public originalDistributorArray: any[] = [];

  constructor(
    @Inject(forwardRef(() => TranslateService)) private translate: TranslateService,
    private localStorageService: LocalStorageService,
    private renderer: Renderer2,
    private cognitoService: CognitoService,
    public validationService: ValidationService,
    public theme: ThemeService,
    public distributorsService: DistributorsService,
    public navigationService: NavigationService,
    public systemMessage: SystemMessageService,
    private filter: FilterService
  ) {
    this.cognitoService.confirmValidUser();
  }

  ngOnInit(): void {
    // Call the getDistributorsList function to retrieve all distributors
    this.getDistributorsList()

    // Set the main content to "clients" by default
    this.mainContent = "clients";

  }

  // Funciton called to reload the page call by refresh button on list
  refreshList() {
    this.localStorageService.addItem('entitiesContent', 'distributors')
    window.location.reload();
  }

  // Function used to get the list of distributors
  async getDistributorsList() {
    await firstValueFrom(this.distributorsService.getDistributors()).then((response: any) => {
      this.distributorsData = response; // Fill the array with customer data
      this.distributorsArray = this.distributorsData; // Assign the client data to the clientArray
      this.distributorsArray = this.distributorsArray.slice(0, this.itemsPerPage);
      this.originalDistributorArray = this.distributorsArray;

      this.loadedItems = this.itemsPerPage;
    }).catch(error => {
      console.error('Error when fetching customer data:', error);
    })
  }

  // Function used in lasy loading to load more distributors in the list
  loadMoreItems() {
    // Slice the remaining items based on the loaded items and items per page
    const remainingItems = this.distributorsData.slice(this.loadedItems, this.loadedItems + this.itemsPerPage);

    // Add more items to the displayed list by concatenating the remaining items
    this.distributorsArray = this.distributorsArray.concat(remainingItems);

    // Update the count of loaded items
    this.loadedItems += this.itemsPerPage;
  }

  ngAfterViewInit(): void {
    // Call the function to detect if the user has reached the end of the page on a mobile device
    this.detectEndOfPageOnMobile();
  }

  // Function used to made a search in the list
  search(){
    // Check if there's a filterBy variable and set it to it's default value
    if(this.filterBy === ''){
      this.filterBy = 'legal_name';
    }

    this.distributorsArray = this.filter.search(this.userFilter, this.distributorsArray, this.originalDistributorArray, this.filterBy);
  }

  // Function to detect if the user has reached the end of the page on a mobile device
  detectEndOfPageOnMobile(): void {
    // Check if the user is using a mobile device
    if (this.isMobileDevice()) {
      // Listen for the 'touchmove' event to track scrolling on the document
      this.renderer.listen('document', 'touchmove', (event: TouchEvent) => {
        // Get the current scroll position, window height, and document height
        const scrollPosition = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0;
        const documentHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight,
          document.documentElement.clientHeight
        );
        // Check if the user has scrolled to the end of the page and not currently loading more data
        if (scrollPosition + windowHeight >= documentHeight && !this.loadingMoreData) {
          this.loadMoreItems();
        }
      });
    }
  }

  // Function to determine if the current device is a mobile device
  isMobileDevice(): boolean {
    // Get the user agent string and convert it to lowercase
    const userAgent = navigator.userAgent.toLowerCase();

    // Check if the user agent matches any common mobile device keywords
    return /mobile|android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  }

  // Host listener to detect scroll events on the window
  @HostListener('window:scroll', []) onScroll(): void {
    // Get the height of the visible window area
    const windowHeight = 'innerHeight' in window ? window.innerHeight : document.documentElement.offsetHeight;

    // Get references to the document's body and html elements
    const body = document.body;
    const html = document.documentElement;

    // Calculate the maximum document height using various metrics
    const documentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);

    // Get the current scroll position
    const scrollPosition = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // Check if the user has scrolled near the bottom of the page
    if (documentHeight - windowHeight - scrollPosition < 100 && !this.loadingMoreData && !this.filterOn) {
      this.loadMoreItems()
    }
  }

  // Function to format a number sequence as a phone number
  formatarNumero(numberSequence: string | undefined | null): string {
    // Check if the numberSequence is undefined or null
    if (numberSequence === undefined || numberSequence === null) {
      return ''; // Return an empty string, or handle the case appropriately
    }

    // Remove non-numeric characters from the numberSequence
    const justNumbers = numberSequence.replace(/\D/g, '');

    // Check if the cleaned numberSequence has a length of 10 (typical for phone numbers)
    if (justNumbers.length === 10) {
      // Split the number into three parts: (XXX) XXX-XXXX
      const parte1 = justNumbers.slice(0, 3);
      const parte2 = justNumbers.slice(3, 6);
      const parte3 = justNumbers.slice(6);

      // Return the formatted phone number
      return `(${parte1}) ${parte2}-${parte3}`;
    } else {
      // If the length is not 10, return the original numberSequence
      return numberSequence;
    }
  }
}
